import React, { useState, useMemo } from 'react';
import { DollarSign, Calendar, Percent, Target } from 'lucide-react';
import CalculatorInput from '../shared/CalculatorInput';
import ResultCard from '../shared/ResultCard';
import Chart from '../shared/Chart';

const RetirementCalculator: React.FC = () => {
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(65);
  const [currentSavings, setCurrentSavings] = useState(50000);
  const [monthlyContribution, setMonthlyContribution] = useState(1000);
  const [annualReturn, setAnnualReturn] = useState(7);
  const [desiredIncome, setDesiredIncome] = useState(80000);

  const calculatedData = useMemo(() => {
    const years = retirementAge - currentAge;
    const monthlyRate = annualReturn / 100 / 12;
    const months = years * 12;
    const data = [];

    let balance = currentSavings;
    let totalContributed = currentSavings;

    for (let i = 0; i <= months; i++) {
      if (i > 0) {
        balance = balance * (1 + monthlyRate) + monthlyContribution;
        totalContributed += monthlyContribution;
      }

      if (i % 12 === 0) {
        data.push({
          year: currentAge + i / 12,
          balance: Math.round(balance),
          contributed: Math.round(totalContributed),
        });
      }
    }

    return data;
  }, [currentAge, retirementAge, currentSavings, monthlyContribution, annualReturn]);

  const finalBalance = calculatedData[calculatedData.length - 1]?.balance || 0;
  const yearsOfRetirement = Math.round(finalBalance / desiredIncome);

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden p-6 sm:p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Retirement Calculator</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <CalculatorInput
          label="Current Age"
          value={currentAge}
          onChange={setCurrentAge}
          icon={<Calendar size={20} />}
          suffix="years"
        />
        <CalculatorInput
          label="Retirement Age"
          value={retirementAge}
          onChange={setRetirementAge}
          icon={<Target size={20} />}
          suffix="years"
        />
        <CalculatorInput
          label="Current Savings"
          value={currentSavings}
          onChange={setCurrentSavings}
          icon={<DollarSign size={20} />}
          prefix="$"
        />
        <CalculatorInput
          label="Monthly Contribution"
          value={monthlyContribution}
          onChange={setMonthlyContribution}
          icon={<DollarSign size={20} />}
          prefix="$"
        />
        <CalculatorInput
          label="Expected Annual Return"
          value={annualReturn}
          onChange={setAnnualReturn}
          icon={<Percent size={20} />}
          suffix="%"
        />
        <CalculatorInput
          label="Desired Annual Income"
          value={desiredIncome}
          onChange={setDesiredIncome}
          icon={<DollarSign size={20} />}
          prefix="$"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <ResultCard
          title="Retirement Savings"
          value={finalBalance}
          description="Projected savings at retirement"
          trend="up"
        />
        <ResultCard
          title="Years of Income"
          value={yearsOfRetirement}
          suffix="years"
          description="Based on desired annual income"
          trend="up"
        />
      </div>

      <Chart data={calculatedData} />
    </div>
  );
};

export default RetirementCalculator;